/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  getDailyBriefing,
  getWeeklyBriefing,
  getMonthlyBriefing,
  BriefingEvent,
  EventConflict,
  WeeklyBriefingData,
  MonthlyBriefingData,
} from '../services';
import { useEventsStore } from '../stores';

export interface ScheduleEvent {
  id: string;
  time: string;
  timeRaw?: string;
  endTime?: string;
  endTimeRaw?: string;
  title: string;
  memberName?: string | null;
  location?: string | null;
  address?: string | null;
  guardianNote?: string | null;
  contextNotes?: string[];
  isPast: boolean;
  isCurrent: boolean;
  source?: string;
  planName?: string | null;
  planId?: number | null;
  activityStatus?: 'pending' | 'completed' | 'skipped' | null;
  itemId?: number | null;
  latitude?: number | null;
  longitude?: number | null;
  eventStatus?: string | null;
  calendarName?: string | null;
  calendarColor?: string | null;
}

export type TimeRange = 'today' | 'week' | 'month';

interface GroupableEvent {
  start_time?: string;
  date?: string;
  time?: string;
  title: string;
  member_name?: string | null;
}

interface GroupedEvent {
  start_time: string;
  title: string;
  member_name?: string | null;
}

// Simple cache for API responses
const apiCache: Map<string, { data: unknown; timestamp: number }> = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const getCached = <T,>(key: string): T | null => {
  const cached = apiCache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data as T;
  }
  return null;
};

const setCache = (key: string, data: unknown) => {
  // Don't cache null/undefined (error path). Empty arrays are valid ("no events today").
  if (data == null) return;
  apiCache.set(key, { data, timestamp: Date.now() });
};

export const clearScheduleCache = () => apiCache.clear();

// Helper to get day name from date
export const getDayName = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { weekday: 'short' });
};

export interface UseScheduleReturn {
  // State
  activeTab: TimeRange;
  dateOffset: number;
  isLoading: boolean;
  briefingFailed: boolean;
  viewMode: 'list' | 'map';
  events: ScheduleEvent[];
  currentDateStr: string;
  hasEventsForDate: boolean;
  weeklyData: WeeklyBriefingData | null;
  monthlyData: MonthlyBriefingData | null;
  weather: string | null;
  weatherCode: number | null;
  weatherHigh: number | null;
  expandedWeeks: Set<number>;
  expandedDays: Set<string>;
  memberFilter: string | null;
  conflicts: EventConflict[];

  // Refs
  briefContentRef: React.RefObject<HTMLDivElement>;
  currentEventRef: React.RefObject<HTMLDivElement>;

  // Schedule actions
  handleTabChange: (tab: TimeRange) => Promise<void>;
  navigatePeriod: (direction: 'next' | 'prev') => Promise<void>;
  setViewMode: (mode: 'list' | 'map') => void;
  toggleWeekExpanded: (weekIdx: number) => void;
  toggleDayExpanded: (dateStr: string) => void;
  setMemberFilter: (member: string | null) => void;
  formatDateHeader: () => string;
  formatEventTime: (timeStr: string) => string;
  getUniqueFamilyMembers: () => string[];
  groupEventsByDay: (events: GroupableEvent[]) => Map<string, GroupedEvent[]>;
  getConflictsForDate: (dateStr: string) => EventConflict[];
  isEventInConflict: (eventTitle: string) => boolean;

  // For parent integration
  getTodayString: () => string;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setBriefingFailed: React.Dispatch<React.SetStateAction<boolean>>;
  setTodayBriefing: (
    todayStr: string,
    events: ScheduleEvent[],
    weather: string | null,
    weatherCode: number | null,
    weatherHigh: number | null
  ) => void;
}

export function useSchedule(): UseScheduleReturn {
  const [eventsByDate, setEventsByDate] = useState<Map<string, ScheduleEvent[]>>(new Map());
  const storeConflicts = useEventsStore((s) => s.conflicts) || [];
  const conflicts = useMemo(() => {
    const now = new Date();
    const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    return storeConflicts.filter((conflict) => {
      const overlapStart = new Date(conflict.overlap_start);
      return overlapStart >= now && overlapStart <= sevenDaysFromNow;
    });
  }, [storeConflicts]);
  const [weather, setWeather] = useState<string | null>(null);
  const [weatherCode, setWeatherCode] = useState<number | null>(null);
  const [weatherHigh, setWeatherHigh] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [briefingFailed, setBriefingFailed] = useState(false);
  const [activeTab, setActiveTab] = useState<TimeRange>('today');

  const [dateOffset, setDateOffset] = useState(0);
  const [weeklyData, setWeeklyData] = useState<WeeklyBriefingData | null>(null);
  const [monthlyData, setMonthlyData] = useState<MonthlyBriefingData | null>(null);

  const [expandedWeeks, setExpandedWeeks] = useState<Set<number>>(new Set());
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
  const [memberFilter, setMemberFilter] = useState<string | null>(null);

  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');

  const briefContentRef = useRef<HTMLDivElement>(null);
  const currentEventRef = useRef<HTMLDivElement>(null);
  const hasScrolledToCurrentRef = useRef(false);

  const getTodayString = (): string => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  };

  const getDateStringForOffset = (offset: number): string => {
    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() + offset);
    return `${targetDate.getFullYear()}-${String(targetDate.getMonth() + 1).padStart(2, '0')}-${String(targetDate.getDate()).padStart(2, '0')}`;
  };

  const currentDateStr = getDateStringForOffset(dateOffset);
  const events = eventsByDate.get(currentDateStr) || [];
  const hasEventsForDate = eventsByDate.has(currentDateStr);

  // Auto-scroll to current/upcoming event when today's schedule loads
  useEffect(() => {
    if (activeTab === 'today' && dateOffset === 0 && events.length > 0 && !isLoading && !hasScrolledToCurrentRef.current) {
      setTimeout(() => {
        if (currentEventRef.current) {
          currentEventRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          hasScrolledToCurrentRef.current = true;
        }
      }, 100);
    }
  }, [events, isLoading, activeTab, dateOffset]);

  // Reset member filter when tab changes
  useEffect(() => {
    setMemberFilter(null);
    setExpandedWeeks(new Set());
  }, [activeTab]);

  // Load daily data with offset
  const loadDailyData = async (offset: number) => {
    const dateStr = getDateStringForOffset(offset);

    try {
      const briefingData = await getDailyBriefing(dateStr);

      if (briefingData) {
        const loadedEvents = briefingData.events
          .map((e: BriefingEvent) => ({
            id: e.id,
            time: e.time_display || e.time,
            timeRaw: e.time,
            endTime: e.end_time_display || e.end_time,
            endTimeRaw: e.end_time,
            title: e.title,
            memberName: e.member_name,
            location: e.location,
            address: e.address ?? null,
            guardianNote: e.guardian_note ?? null,
            contextNotes: e.context_notes ?? [],
            isPast: e.is_past,
            isCurrent: e.is_current,
            source: e.source,
            planName: e.plan_name,
            planId: e.plan_id,
            itemId: e.itinerary_item_id ?? null,
            activityStatus: (e.activity_status as 'pending' | 'completed' | 'skipped') ?? null,
            latitude: e.latitude ?? null,
            longitude: e.longitude ?? null,
            eventStatus: e.event_status ?? null,
            calendarName: e.calendar_name ?? null,
            calendarColor: e.calendar_color ?? null,
          }))
          .sort((a: ScheduleEvent, b: ScheduleEvent) => (a.timeRaw || a.time || '').localeCompare(b.timeRaw || b.time || ''));
        setEventsByDate(prev => {
          const newMap = new Map(prev);
          newMap.set(dateStr, loadedEvents);
          return newMap;
        });
        setWeather(briefingData.weather?.summary || null);
        setWeatherCode(briefingData.weather_code ?? null);
        setWeatherHigh(briefingData.weather_high ?? null);
      } else {
        setEventsByDate(prev => new Map(prev).set(dateStr, []));
      }
    } catch (err) {
      console.error('Failed to load daily data:', err);
      setEventsByDate(prev => new Map(prev).set(dateStr, []));
    }
  };

  // Load weekly data with offset
  const loadWeeklyData = async (offset: number, prefetchOnly = false) => {
    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() + (offset * 7));
    const dateStr = `${targetDate.getFullYear()}-${String(targetDate.getMonth() + 1).padStart(2, '0')}-${String(targetDate.getDate()).padStart(2, '0')}`;
    const cacheKey = `weekly_${dateStr}`;

    const cached = getCached<WeeklyBriefingData>(cacheKey);
    if (cached) {
      if (!prefetchOnly) setWeeklyData(cached);
      return;
    }

    try {
      const weekData = await getWeeklyBriefing(dateStr);
      setCache(cacheKey, weekData);
      if (!prefetchOnly) setWeeklyData(weekData);
    } catch (err) {
      console.error('Failed to load weekly data:', err);
    }
  };

  // Load monthly data with offset
  const loadMonthlyData = async (offset: number, prefetchOnly = false) => {
    const targetDate = new Date();
    targetDate.setMonth(targetDate.getMonth() + offset);
    const month = targetDate.getMonth() + 1;
    const year = targetDate.getFullYear();
    const cacheKey = `monthly_${year}_${month}`;

    const cached = getCached<MonthlyBriefingData>(cacheKey);
    if (cached) {
      if (!prefetchOnly) setMonthlyData(cached);
      return;
    }

    try {
      const monthData = await getMonthlyBriefing(month, year);
      setCache(cacheKey, monthData);
      if (!prefetchOnly) setMonthlyData(monthData);
    } catch (err) {
      console.error('Failed to load monthly data:', err);
    }
  };

  const handleTabChange = async (tab: TimeRange) => {
    setActiveTab(tab);
    setDateOffset(0);
    setIsLoading(true);

    try {
      if (tab === 'today') {
        await loadDailyData(0);
      } else if (tab === 'week') {
        const weekData = await getWeeklyBriefing();
        setWeeklyData(weekData);
      } else if (tab === 'month') {
        const now = new Date();
        const monthData = await getMonthlyBriefing(now.getMonth() + 1, now.getFullYear());
        setMonthlyData(monthData);
      }
    } catch (err) {
      console.error(`Failed to load ${tab} data:`, err);
    } finally {
      setIsLoading(false);
    }
  };

  const navigatePeriod = async (direction: 'next' | 'prev') => {
    const newOffset = direction === 'next' ? dateOffset + 1 : dateOffset - 1;
    setDateOffset(newOffset);
    setIsLoading(true);

    try {
      if (activeTab === 'today') {
        await loadDailyData(newOffset);
      } else if (activeTab === 'week') {
        await loadWeeklyData(newOffset);
      } else if (activeTab === 'month') {
        await loadMonthlyData(newOffset);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Prefetch adjacent periods
  const prefetchAdjacentPeriods = useCallback(() => {
    if (activeTab === 'week') {
      loadWeeklyData(dateOffset - 1, true);
      loadWeeklyData(dateOffset + 1, true);
    } else if (activeTab === 'month') {
      loadMonthlyData(dateOffset - 1, true);
      loadMonthlyData(dateOffset + 1, true);
    }
  }, [activeTab, dateOffset]);

  // Prefetch adjacent periods when data loads
  useEffect(() => {
    if ((activeTab === 'week' && weeklyData) || (activeTab === 'month' && monthlyData)) {
      const timer = setTimeout(prefetchAdjacentPeriods, 500);
      return () => clearTimeout(timer);
    }
  }, [weeklyData, monthlyData, activeTab, prefetchAdjacentPeriods]);

  const getUniqueFamilyMembers = useCallback((): string[] => {
    const members = new Set<string>();
    if (activeTab === 'month' && monthlyData) {
      monthlyData.weeks.forEach(week => {
        week.events?.forEach(event => {
          if (event.member_name) members.add(event.member_name);
        });
      });
    } else if (activeTab === 'week' && weeklyData) {
      weeklyData.days.forEach(day => {
        day.events?.forEach(event => {
          if (event.member_name) members.add(event.member_name);
        });
      });
    }
    return Array.from(members).sort();
  }, [activeTab, monthlyData, weeklyData]);

  const groupEventsByDay = useCallback((evts: GroupableEvent[]): Map<string, GroupedEvent[]> => {
    const grouped = new Map<string, GroupedEvent[]>();
    evts.forEach(event => {
      const startTime = event.start_time || (event.date && event.time ? `${event.date}T${event.time}` : '');
      if (!startTime) return;
      const dateKey = startTime.split('T')[0];
      if (!grouped.has(dateKey)) {
        grouped.set(dateKey, []);
      }
      grouped.get(dateKey)!.push({
        start_time: startTime,
        title: event.title,
        member_name: event.member_name,
      });
    });
    grouped.forEach((dayEvents) => {
      dayEvents.sort((a, b) => a.start_time.localeCompare(b.start_time));
    });
    return grouped;
  }, []);

  const toggleWeekExpanded = (weekIdx: number) => {
    setExpandedWeeks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(weekIdx)) {
        newSet.delete(weekIdx);
      } else {
        newSet.add(weekIdx);
      }
      return newSet;
    });
  };

  const toggleDayExpanded = (dateStr: string) => {
    setExpandedDays(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dateStr)) {
        newSet.delete(dateStr);
      } else {
        newSet.add(dateStr);
      }
      return newSet;
    });
  };

  const getConflictsForDate = useCallback((dateStr: string): EventConflict[] => {
    return conflicts.filter(conflict => {
      const conflictDate = conflict.overlap_start.split('T')[0];
      return conflictDate === dateStr;
    });
  }, [conflicts]);

  const isEventInConflict = useCallback((eventTitle: string): boolean => {
    return conflicts.some(conflict =>
      conflict.event_one_title === eventTitle || conflict.event_two_title === eventTitle
    );
  }, [conflicts]);

  const formatDateHeader = (): string => {
    if (activeTab === 'today') {
      const targetDate = new Date();
      targetDate.setDate(targetDate.getDate() + dateOffset);
      const isToday = dateOffset === 0;
      const isTomorrow = dateOffset === 1;
      const isYesterday = dateOffset === -1;

      let prefix = '';
      if (isToday) prefix = '';
      else if (isTomorrow) prefix = 'Tomorrow, ';
      else if (isYesterday) prefix = 'Yesterday, ';

      return prefix + targetDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric'
      });
    } else if (activeTab === 'week' && weeklyData) {
      const startDate = new Date(weeklyData.start_date);
      const endDate = new Date(weeklyData.end_date);
      return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    } else if (activeTab === 'month' && monthlyData) {
      return `${monthlyData.month_name} ${monthlyData.year}`;
    }

    return new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatEventTime = (timeStr: string): string => {
    if (timeStr.includes('T') || timeStr.includes('-')) {
      try {
        const date = new Date(timeStr);
        return date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
      } catch {
        return timeStr;
      }
    }
    return timeStr;
  };

  const setTodayBriefing = useCallback((
    todayStr: string,
    todayEvents: ScheduleEvent[],
    weatherSummary: string | null,
    wCode: number | null,
    wHigh: number | null
  ) => {
    setEventsByDate(prev => new Map(prev).set(todayStr, todayEvents));
    setWeather(weatherSummary);
    setWeatherCode(wCode);
    setWeatherHigh(wHigh);
  }, []);

  return {
    activeTab,
    dateOffset,
    isLoading,
    briefingFailed,
    viewMode,
    events,
    currentDateStr,
    hasEventsForDate,
    weeklyData,
    monthlyData,
    weather,
    weatherCode,
    weatherHigh,
    expandedWeeks,
    expandedDays,
    memberFilter,
    conflicts,
    briefContentRef,
    currentEventRef,
    handleTabChange,
    navigatePeriod,
    setViewMode,
    toggleWeekExpanded,
    toggleDayExpanded,
    setMemberFilter,
    formatDateHeader,
    formatEventTime,
    getUniqueFamilyMembers,
    groupEventsByDay,
    getConflictsForDate,
    isEventInConflict,
    getTodayString,
    setIsLoading,
    setBriefingFailed,
    setTodayBriefing,
  };
}
